import React, { useState, useMemo } from 'react';
import {
    FileText, Calendar, Tag, ChevronDown, Check,
    GitCompare, X, History, Code, GitBranch, Settings
} from 'lucide-react';
import Modal from '../Modal';

/**
 * Contract card with simplified actions and refined delete modal
 */
const ContractCard = ({
    contract,
    onExport,
    onCopy,
    onCompare,
    onDelete,
    onTagClick,
    onViewHistory,
    onFork,
    onEdit,
    addToast
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showForkModal, setShowForkModal] = useState(false);
    const [forkName, setForkName] = useState('');

    // Calculate field count from schema
    const fieldCount = useMemo(() => {
        const countFields = (obj, depth = 0) => {
            if (depth > 50) return 0;
            if (typeof obj !== 'object' || obj === null) return 0;
            if (Array.isArray(obj)) {
                return obj.length > 0 ? countFields(obj[0], depth + 1) : 0;
            }
            let count = Object.keys(obj).length;
            for (const value of Object.values(obj)) {
                count += countFields(value, depth + 1);
            }
            return count;
        };
        return countFields(contract.schema_content);
    }, [contract.schema_content]);

    // Format relative time
    const timeAgo = useMemo(() => {
        const date = new Date(contract.updated_at);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }, [contract.updated_at]);

    const handleCopy = async (e) => {
        e?.stopPropagation();
        await onCopy(contract);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Confirmation modal removed in favor of optimistic undo

    const handleForkClick = (e) => {
        e?.stopPropagation();
        setForkName(`${contract.name} (Fork)`);
        setShowForkModal(true);
    };

    const handleForkConfirm = () => {
        if (onFork && forkName.trim()) {
            onFork(contract, forkName.trim());
        }
        setShowForkModal(false);
        setForkName('');
    };

    const tags = contract.tags || [];

    return (
        <>
            <div className="relative group transition-all rounded-xl border border-[#3c3c3c] bg-[#252526] hover:border-[#4c4c4c]">
                {/* Delete Button - Absolute Positioned Top Right */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(contract);
                    }}
                    className="absolute top-3 right-3 p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-[#3c3c3c] transition-all z-10 opacity-0 group-hover:opacity-100"
                    title="Delete Contract"
                >
                    <X size={14} />
                </button>

                {/* Header */}
                <div className="p-4">
                    <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 rounded-md bg-[#3c3c3c] text-gray-300 shrink-0">
                            <FileText size={20} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2 flex-wrap">
                                <h3 className="font-semibold text-white text-base truncate" title={contract.name}>
                                    {contract.name}
                                </h3>
                                {/* Show "Forked from X" inline with title if description contains it */}
                                {contract.description && contract.description.startsWith('Forked from') && (
                                    <span className="text-xs text-gray-500 whitespace-nowrap">
                                        {contract.description.split(':')[0]}
                                    </span>
                                )}
                            </div>
                            {/* Only show description if it's NOT a forked-from message */}
                            {contract.description && !contract.description.startsWith('Forked from') && (
                                <p className="text-xs text-gray-400 truncate mt-0.5">
                                    {contract.description}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Tags - filter out forked/forked-from-version since it's shown inline */}
                    {tags.filter(tag => tag !== 'forked' && tag !== 'forked-from-version').length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                            {tags.filter(tag => tag !== 'forked' && tag !== 'forked-from-version').map((tag, idx) => (
                                <button
                                    key={idx}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onTagClick?.(tag);
                                    }}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider bg-purple-900/30 text-purple-300 hover:bg-purple-900/50 transition-colors"
                                >
                                    <Tag size={9} />
                                    {tag}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                        <span className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                            {fieldCount} fields
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {timeAgo}
                        </span>
                    </div>

                    {/* Action Buttons - Simplified & Less "Janky" */}
                    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#333]">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(!isExpanded);
                            }}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border ${isExpanded
                                ? 'bg-blue-900/20 text-blue-300 border-blue-800'
                                : 'bg-[#3c3c3c] border-[#4c4c4c] text-gray-400 hover:bg-[#4c4c4c]'
                                }`}
                        >
                            <Code size={12} />
                            View
                            <ChevronDown size={10} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onCompare(contract);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-blue-400 hover:bg-blue-900/40"
                        >
                            <GitCompare size={12} />
                            Compare
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewHistory?.(contract);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-purple-400 hover:bg-purple-900/40"
                        >
                            <History size={12} />
                            History
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(contract);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-[#4c4c4c] text-gray-400 hover:bg-[#4c4c4c]"
                            title="Contract Settings"
                        >
                            <Settings size={12} />
                            Settings
                        </button>

                        <button
                            onClick={handleForkClick}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-purple-400 hover:bg-purple-900/40"
                            title="Fork Contract"
                        >
                            <GitBranch size={12} />
                            Fork
                        </button>
                    </div>
                </div>

                {/* Expandable Schema Content */}
                {isExpanded && (
                    <div className="border-t border-[#333] bg-[#1f1f1f] rounded-b-xl overflow-hidden animate-in slide-in-from-top-1 duration-200">
                        <div className="p-4">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                                    <Code size={12} />
                                    Schema Content
                                </span>
                                <button
                                    className="text-xs text-blue-400 cursor-pointer flex items-center gap-1"
                                    onClick={handleCopy}
                                >
                                    {copied ? <Check size={12} /> : <FileText size={12} />}
                                    {copied ? 'Copied!' : 'Copy JSON'}
                                </button>
                            </div>
                            <div className="bg-[#1a1a1a] rounded-lg border border-[#333] p-3 overflow-x-auto max-h-64 shadow-inner">
                                <pre className="text-[11px] font-mono leading-relaxed text-gray-300">
                                    {JSON.stringify(contract.schema_content, null, 2)}
                                </pre>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal removed */}

            {/* Fork Contract Modal */}
            <Modal
                isOpen={showForkModal}
                onClose={() => setShowForkModal(false)}
                title="Fork Contract"
                footer={
                    <>
                        <button
                            onClick={() => setShowForkModal(false)}
                            className="px-4 py-2 text-[#cccccc] hover:bg-[#3c3c3c] rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleForkConfirm}
                            disabled={!forkName.trim()}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Fork Contract
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-purple-900/20 rounded-lg border border-purple-800">
                        <GitBranch className="text-purple-400" size={20} />
                        <div>
                            <p className="text-sm font-medium text-purple-300">
                                Forking: {contract.name}
                            </p>
                            <p className="text-xs text-purple-400">
                                Creates a new contract with the same schema content
                            </p>
                        </div>
                    </div>

                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-[#cccccc] mb-1">
                            New Contract Name
                        </label>
                        <input
                            type="text"
                            value={forkName}
                            onChange={(e) => setForkName(e.target.value)}
                            placeholder="e.g., Company 850 v2 (Fork)"
                            className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#252526] text-white focus:ring-2 focus:ring-purple-500 outline-none"
                            autoFocus
                        />
                    </div>

                    <div className="bg-[#1e1e1e] p-3 rounded-lg text-xs text-gray-400">
                        <p>
                            <strong>Note:</strong> The forked contract will start with version 1 and won't share
                            version history with the original contract.
                        </p>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ContractCard;
